import { get, includes, isNull } from 'lodash-es';

import { STATUS_NO_CONTENT } from 'portal-base/src/base/constants';
import { setCSRFTokenToCookie } from 'portal-base/src/stores/PortalStore';

/**
 * PWA override of RequestHandler that removes the reload-on-401 behavior.
 * The PWA operates without authentication, so 401 responses from protected
 * endpoints should not trigger a page reload loop.
 */
export default function requestHandler({ getCSRFToken = false } = {}) {
    return (target, name, descriptor) => {
        const fn = descriptor.value;
        const newFn = function () {
            return fn.apply(target, arguments).then(response => {
                let csrfOk = true;
                if (name === 'login' || name === 'csrfToken' || getCSRFToken) {
                    const token = response.headers.get('x-csrf-token');
                    !isNull(token) && setCSRFTokenToCookie(token);
                    if (includes(document.cookie, 'cc29531a7f7c33d76b9e9a07705a327851ca8b24=null')) {
                        csrfOk = false;
                    }
                } else if (name === 'logout') {
                    document.cookie = 'cc29531a7f7c33d76b9e9a07705a327851ca8b24=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
                }

                if (name === 'csrfToken' && !response.ok && csrfOk) {
                    return response.json();
                }

                if (response.status === STATUS_NO_CONTENT) {
                    return null;
                }

                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    return response.json().then(json => {
                        if (response.ok) {
                            return json;
                        }
                        // PWA: Do NOT reload on 401/40100 — just throw the error
                        throw json;
                    });
                }
                if (contentType && (contentType.includes('application/xml') || contentType.includes('text/xml'))) {
                    return response.text().then(text => {
                        const parser = new DOMParser();
                        const xml = parser.parseFromString(text, 'application/xml');
                        if (response.ok) {
                            return xml;
                        }
                        throw xml;
                    });
                }
                if (contentType && contentType.includes('text/')) {
                    return response.text().then(text => {
                        if (response.ok) {
                            return text;
                        }
                        throw new Error(text);
                    });
                }
                return response.blob().then(blob => {
                    if (response.ok) {
                        return blob;
                    }
                    throw new Error('Unexpected content type');
                });
            });
        };
        descriptor.value = newFn;
        return descriptor;
    };
}
